import { useEffect, useRef, useState } from "react";
import type { EditorStore } from "../lib/store.ts";
import type { AssetInfo } from "../lib/api.ts";
import { addElement, newImageElement, newVideoElement, sceneAt, sceneOf, updateScene } from "../lib/ops.ts";
import { t } from "../lib/i18n.ts";
import { Err, Spinner, msgOf, useApp } from "./common.tsx";
import { useAudio } from "./audio.tsx";
import { addClip } from "../lib/audio.ts";

let imgInputCounter = 0;
/** Pictures the person imported (PNG, JPEG, WebP, GIF, SVG): upload, tap to put one in the current scene. */
export function ImageLibrary({ store, onAdded, bare }: { store: EditorStore; onAdded?: (id: string) => void; bare?: boolean }) {
  const { api } = useApp();
  const A = useAudio();
  const input = useRef(null as HTMLInputElement | null);
  const [inputId] = useState(() => `img-file-${++imgInputCounter}`);      // several libraries can be on screen: ids must be unique
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null as string | null);
  const pics = (A.list?.assets ?? []).filter((a: AssetInfo) => a.kind === "image");
  const videos = (A.list?.assets ?? []).filter((a: AssetInfo) => a.kind === "video");

  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null as { done: number; total: number } | null);
  /** One or many files (picker, drag and drop, paste). Each file fails on its own with its own message; a single file is placed straight away. */
  async function uploadMany(list: FileList | File[] | null | undefined) {
    const files = Array.from(list ?? []).filter((f: File) => f.size > 0 || f.type);
    if (!files.length) return;
    setBusy(true); setErr(null);
    const failed: string[] = []; let last: AssetInfo | null = null;
    for (let i = 0; i < files.length; i++) {
      setProgress({ done: i, total: files.length });
      try { last = await api.uploadAsset(files[i]!); } catch (e) { failed.push(`${files[i]!.name || "image"}: ${msgOf(e)}`); }
    }
    setProgress(null);
    try { await A.refresh(); } catch { /* the list refreshes on the next change */ }
    if (failed.length) setErr(failed.slice(0, 3).join("\n") + (failed.length > 3 ? `\n+${failed.length - 3}` : ""));
    if (last && files.length === 1) place(last);
    setBusy(false); if (input.current) input.current.value = "";
  }
  useEffect(() => {      // paste a picture from the clipboard (screenshots, copied images)
    const onPaste = (e: ClipboardEvent) => {
      const t0 = e.target as HTMLElement | null;
      if (t0 && /^(INPUT|TEXTAREA|SELECT)$/.test(t0.tagName)) return;
      const files = Array.from(e.clipboardData?.files ?? []).filter((f: File) => f.type.startsWith("image/"));
      if (files.length) { e.preventDefault(); void uploadMany(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });
  function place(a: AssetInfo) {
    const s = store.getState(), p = s.project; if (!p) return;
    const sel = s.selection;
    const sid = sel.kind === "scene" ? sel.id : sel.kind === "element" ? sceneOf(p, sel.id)?.id : (sceneAt(p, s.timeMs)?.id ?? p.scenes[p.scenes.length - 1]?.id);
    if (!sid) { setErr(t("lib.no_scene")); return; }
    if (a.kind === "video") {
      const scene = p.scenes.find((x) => x.id === sid)!;
      const duration = Math.max(100, a.duration_ms);
      const el = newVideoElement(p, { ...a, duration_ms: duration });
      let next = updateScene(p, sid, { duration_ms: Math.max(scene.duration_ms, duration) });
      next = addElement(next, sid, el);
      if (a.audio_asset_id) {
        next = { ...next, duration_ms: Math.max(next.duration_ms, scene.start_ms + duration) };
        const sound = addClip(next, { id: a.audio_asset_id, name: a.name, duration_ms: duration }, scene.start_ms, "voice");
        if (sound) next = sound.project;
      }
      store.edit(() => next); store.select({ kind: "element", id: el.id }); onAdded?.(el.id); return;
    }
    const el = newImageElement(p, a);
    store.edit((q) => addElement(q, sid, el)); store.select({ kind: "element", id: el.id });
    onAdded?.(el.id);
  }
  async function remove(a: AssetInfo) {
    setErr(null);
    try { await api.deleteAsset(a.id); await A.refresh(); } catch (e) { setErr(msgOf(e)); }
  }
  return (
    <div className="imglib">
      {!bare && <h3 className="h">{t("img.title")}</h3>}
      <input ref={input} type="file" multiple className="sr" id={inputId} accept="image/*,video/mp4,video/quicktime,video/webm,video/x-msvideo,.svg,.bmp,.tif,.tiff,.ico,.heic,.avif,.mp4,.mov,.webm,.avi,.mkv"
             onChange={(e: any) => void uploadMany(e.target.files)} />
      <label htmlFor={inputId} className="btn sm" data-drag={drag} style={{ cursor: busy ? "wait" : "pointer", width: "100%", ...(drag ? { outline: "2px dashed var(--accent, #ff7a1a)", outlineOffset: 2 } : {}) }} aria-disabled={busy}
        onDragOver={(e: any) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e: any) => { e.preventDefault(); setDrag(false); void uploadMany(e.dataTransfer.files); }}>
        {busy ? <><Spinner /> {t("img.uploading")}{progress && progress.total > 1 ? ` ${progress.done + 1}/${progress.total}` : ""}</> : <>＋ {t("img.import")}</>}</label>
      {err && <div style={{ whiteSpace: "pre-line" }}><Err>{err}</Err></div>}
      {pics.length > 0 && <div className="imggrid" role="list">
        {pics.map((a: AssetInfo) => (
          <div key={a.id} className="imgcell" role="listitem">
            <button className="imgthumb" onClick={() => place(a)} title={`${a.name} · ${t("img.add")}`} aria-label={`${t("img.add")} ${a.name}`}>
              <img src={api.assetUrl(a.id)} alt="" loading="lazy" decoding="async" />
            </button>
            <button className="imgdel" onClick={() => void remove(a)} aria-label={`${t("img.delete")} ${a.name}`} title={t("img.delete")}>×</button>
          </div>))}
      </div>}
      {videos.length > 0 && <div className="imggrid" role="list">
        {videos.map((a: AssetInfo) => <div key={a.id} className="imgcell" role="listitem">
          <button className="imgthumb" onClick={() => place(a)} title={`${a.name} · ${t("img.add")}`} aria-label={`${t("img.add")} ${a.name}`}>
            <video src={api.assetUrl(a.id)} muted playsInline preload="metadata" />
          </button>
          <button className="imgdel" onClick={() => void remove(a)} aria-label={`${t("img.delete")} ${a.name}`} title={t("img.delete")}>×</button>
        </div>)}
      </div>}
      {pics.length === 0 && videos.length === 0 && <small className="muted">{t("img.empty")}</small>}
    </div>
  );
}
